import { Add, SaveAlt } from "@mui/icons-material";
import { Box, CircularProgress, Grid, MenuItem, Select, Slide, Tooltip, Typography } from "@mui/material";
import { useGetLeadsQuery, useUpdateAccountMutation } from "api/api-accounts";
import { useGetSalesListQuery } from "api/api-origination";
import { useGetCurrentUserQuery } from "api/api-users-v2";
import { DEFAULT_ROWS, INCOMLEND_INTERNAL_LINK, LEADS } from "codes";
import DataTablev2 from "components/Common/DataTablev2";
import Layout from "components/Common/Layout";
import NoData from "components/Common/NoData";
import SkeletonLoad from "components/Common/SkeletonLoad";
import CreateLead from "components/Company/Lead/CreateLead";
import ImportLead from "components/Company/Lead/ImportLead";
import history from "helpers/history";
import useWidth from "helpers/useWidth";
import useIsMobile from "hooks/useIsMobile";
import moment from "moment";
import { PERMISSIONS } from "permissions";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { clearAll } from "redux/reducers/searchReducer";
import Modal from "components/Common/Modal";

interface ILeadTable {
  id: string,
  internal_name: string;
  country: string;
  name: string;
  sales_owner: string;
  source: string;
  created_at: Date;
  profile: string;
  [key: string]: any; // Allow for dynamic additional columns
}

const Leads = () => {
  const isMobile = useIsMobile();
  const ref = useRef<HTMLDivElement>(null);
  const width = ref ? useWidth(ref) : 1000;
  const [createLead, setCreateLead] = useState<boolean>(false);
  const [importLead, setImportLead] = useState<boolean>(false);
  const searchValue = useAppSelector((state) => state.search.value);
  const [queryParams, setQueryParams] = useState({
    per_page: 10,
    page: 1,
    search: searchValue || undefined,
  });
  const [filters, setFilters] = useState<Record<string, any>>({});

  const { data: salesOwnerOptions} = useGetSalesListQuery(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedLeadForUpdate, setSelectedLeadForUpdate] = useState<{ id: string; account_id: string } | null>(null);
  const [newSalesOwner, setNewSalesOwner] = useState<string | null>(null);
  const [loadingRowId, setLoadingRowId] = useState<string | null>(null);

  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(clearAll());
  }, [dispatch]);

  const { data: curr_user } = useGetCurrentUserQuery();
  const settings = curr_user?.settings ?? {};
  const rowsPerPage = settings?.rowsPerPage ?? DEFAULT_ROWS;
  const hasSearchedOnce = useRef(false);

  const [updateAccount] = useUpdateAccountMutation();
  const APP_PERMISSIONS = useAppSelector(
    (state) => state.appState.user_permission
  );
  const sales_lead_update = !APP_PERMISSIONS.includes(PERMISSIONS.termsheet_approver);

  useEffect(() => {
    if (searchValue && !hasSearchedOnce.current) {
      hasSearchedOnce.current = true;
    }
    if (hasSearchedOnce.current) {
      setQueryParams((prev) => ({ ...prev, page: 1, search: searchValue || undefined }));
    }
  }, [searchValue]);

  const {
    data: leads,
    isFetching,
    refetch,
  } = useGetLeadsQuery(
    {
      leadsparams: `?${new URLSearchParams(
        Object.fromEntries(
          Object.entries(queryParams)
            .filter(([_, value]) => value !== undefined)
        ) as Record<string, string>
      )}`
    },
    { refetchOnMountOrArgChange: true }
  );

  const handleClearFilters = () => {
    setQueryParams({
      per_page: 10,
      page: 1,
      search: undefined,
    });
    setFilters({});
    dispatch(clearAll());
    refetch();
  };

  const openCreateLeadPage = () => setCreateLead(true);
  const closeCreateLeadPage = () => setCreateLead(false);
  const openImportLeadPage = () => setImportLead(true);
  const closeImportLeadPage = () => setImportLead(false);
  const isCreatorMode = createLead || importLead;

  const getLabel = (): string => {
    switch (true) {
      case createLead:
        return "Create a Lead";
      case importLead:
        return "Import Lead";
      default:
        return "Leads";
    }
  };

  const handleRefetch = async () => {
    await refetch();
    setQueryParams({
      per_page: 10,
      page: 1,
      search: undefined
    });
    setFilters({});
    dispatch(clearAll());
  };

  const refetchLeadsWithoutReset = async () => {
    await refetch();
  };

  const formattedLeadsForTable = (data: ILeadTable[] = []) => {
    return data.map((item) => {
      const salesOwnerId = salesOwnerOptions?.find(
        (owner) => `${owner.first_name} ${owner.last_name}` === item.sales_owner
      )?.id;

      const formattedItem: Record<string, any> = {
        // Reorder fields to make internal_name (Lead ID) first
        internal_name: item.internal_name,
        name: item.name,
        // Move profile (Lead Type) to be before country
        profile: item.profile,
        country: item.country,
        sales_owner: {
          id: salesOwnerId || item.sales_owner,
          account_id: item.id
        },
        ...Object.keys(item)
          .filter((key) => !['internal_name', 'name', 'profile', 'country', 'sales_owner'].includes(key))
          .reduce((obj, key) => {
            obj[key] = item[key];
            return obj;
          }, {} as Record<string, any>)
      };

      // Format dates if they exist
      if (formattedItem.created_at) {
        formattedItem.created_at = moment(formattedItem.created_at).format("DD/MM/YYYY, HH:mm:ss");
      }
      return formattedItem;
    });
  };

  const handleFilterSubmit = (filtersString: string) => {
    const filtersObj = JSON.parse(filtersString);
    setFilters(filtersObj);
    const newFilters: Record<string, string> = {};
    for (const key in filtersObj) {
      if (filtersObj[key]?.keywords) {
        if (key === "created_at" && typeof filtersObj[key].keywords === "string") {
          newFilters[key] = filtersObj[key].keywords;
        } else if (Array.isArray(filtersObj[key].keywords)) {
          newFilters[key] = filtersObj[key].keywords.join(",");
        }
      }
    }
    setQueryParams((prev) => ({ ...prev, ...newFilters, page: 1 }));
  };

  const handlePageChange = (newPage: number, newPerPage: number) => {
    setQueryParams((prev) => ({
      ...prev,
      page: newPage,
      per_page: newPerPage
    }));
  };

  const formattedData = formattedLeadsForTable(leads?.leads_details) ?? [];
  const headerLength = formattedData[0] ? Object.keys(formattedData[0]).length : 8;
  const defaultColumnWidth = width ? (width / headerLength) * 1.2 : 200;

  // Consistent text styling with Billings component
  const textSx = {
    fontSize: "1.1em!important",
    lineHeight: "1.5em",
    height: "auto",
    overflow: "hidden",
    maxWidth: "200px", // Reasonable max-width for all columns
    textOverflow: "ellipsis",
    whiteSpace: "nowrap", // Prevents wrapping
    wordWrap: "break-word",
    minWidth: isMobile ? "100%" : "10vw",
    display: "block", // Ensures ellipsis works properly
  };

  // Dynamic custom columns based on data
  const customColumns = formattedData[0] ? Object.keys(formattedData[0]).reduce((acc, key) => {
    // Special handling for profile column to rename it and place before country
    if (key === 'profile') {
      acc[key] = {
        [key]: 'Lead Type',
        type: "text",
        flexGrow: 1,
        renderCell: ({ value }: { value: any }) => (
          <Typography sx={textSx} title={String(value)}>
            {value || "None"}
          </Typography>
        ),
        disableClick: true
      };
      return acc;
    }

    // Special handling for name column to make it clickable
    if (key === 'name') {
      acc[key] = {
        [key]: 'Name',
        type: "text",
        flexGrow: 1,
        renderCell: ({ value }: { value: any }) => (
          <Typography
            sx={{
              ...textSx,
              textDecoration: 'underline',
              color: 'primary.main',
              cursor: 'pointer',
              '&:hover': {
                color: 'primary.dark'
              }
            }}
            title={String(value)}
          >
            {value || "None"}
          </Typography>
        ),
        link: {
          href: (params: ILeadTable) =>
            `${INCOMLEND_INTERNAL_LINK}${LEADS}/${params.internal_name}`,
          target: "_self",
        }
      };
      return acc;
    }

    // Special handling for internal_name to show as underlined Lead ID
    if (key === 'internal_name') {
      acc[key] = {
        [key]: 'Lead ID',
        type: "text",
        flexGrow: 1,
        renderCell: ({ value }: { value: any }) => (
          <Typography
            sx={{
              ...textSx,
              textDecoration: 'underline',
              color: 'primary.main',
              cursor: 'pointer',
              '&:hover': {
                color: 'primary.dark'
              }
            }}
            title={String(value)}
          >
            {value || "None"}
          </Typography>
        ),
        link: {
          href: (params: ILeadTable) =>
            `${INCOMLEND_INTERNAL_LINK}${LEADS}/${params.internal_name}`,
          target: "_self",
        }
      };
      return acc;
    }

    // Special handling for sales_owner
    if (key === 'sales_owner') {
      acc[key] = {
        [key]: 'Sales Owner',
        type: "select",
        flexGrow: 1,
        renderCell: ({ value }: { value: { id: string; account_id: string } }) => {
          const tooltipText = "Only Head Of Sales Change";
          return (
            <Tooltip title={sales_lead_update ? tooltipText : ""} placement="top" arrow>
              {loadingRowId === value.account_id ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <CircularProgress size={24} />
              </Box>
              ) : (
                <Select
                  value={value.id}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setSelectedLeadForUpdate(value);
                    setNewSalesOwner(newValue);
                    setShowApprovalModal(true);
                  }}
                  fullWidth
                  variant="outlined"
                  sx={{ minWidth: 120 }}
                  disabled={sales_lead_update}
                >
                  {salesOwnerOptions?.map((option) => (
                    <MenuItem key={String(option.id)} value={option.id}>
                      {option.first_name} {option.last_name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </Tooltip>

          );
        },
        disableClick: true,
      };
      return acc;
    }

    // Default column configuration - all other columns are not clickable
    acc[key] = {
      [key]: key.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
      type: key.includes('at') ? "text" : "text",
      flexGrow: 1,
      disableClick: true, // Disable clicking for all other columns
      renderCell: ({ value }: { value: any }) => (
        <Typography sx={textSx} title={String(value)}>
          {value || "None"}
        </Typography>
      )
    };

    return acc;
  }, {} as Record<string, any>) : {};

  return (
    <Slide in direction="left">
      <Box sx={{ flexGrow: 1, p: 0 }}>
        <Layout
          sx={{ padding: "1px" }}
          title={getLabel()}
          textHeadingOptions={{
            fontWeight: "bolder",
            level: 2,
            fontSize: isMobile ? "1.2em" : "1.5em",
          }}
          headerConfig={{
            syncAction: isCreatorMode ? undefined : handleRefetch,
            left: 4,
            mid: 0,
            right: isCreatorMode ? 2 : 4,
            xs: {
              left: isCreatorMode ? 7 : 6,
              mid: 0,
              right: isCreatorMode ? 5 : 6,
            },
            alignItems: "center",
          }}
          primary={
            !importLead
              ? {
                onClick: createLead ? closeCreateLeadPage : openCreateLeadPage,
                isCancel: createLead,
                startIcon: createLead ? undefined : <Add />,
                children: createLead ? "Cancel" : "Lead",
                variant: "contained",
                fullWidth: true,
              }
              : undefined
          }
          secondary={
            !createLead
              ? {
                onClick: importLead ? closeImportLeadPage : openImportLeadPage,
                fullWidth: true,
                startIcon: importLead ? undefined : <SaveAlt />,
                children: importLead ? "Cancel" : "Import Lead",
                color: importLead ? "error" : "primary",
                variant: importLead ? "contained" : "outlined",
              }
              : undefined
          }
          mode="default"
        >
          {createLead && (
            <CreateLead
              onEnd={(internal_name) => {
                closeCreateLeadPage();
                if (internal_name)
                  history.push(
                    `${INCOMLEND_INTERNAL_LINK}${LEADS}/${internal_name}`
                  );
              }}
            />
          )}
          {importLead && <ImportLead />}
          {!createLead && !importLead && (
            <Grid item xs={12} ref={ref} sx={{ margin: "5px" }}>
              <Grid container spacing={1}>
                {isFetching && <SkeletonLoad bars={10} />}
                {!isFetching && formattedData && formattedData.length > 0 ? (
                    <DataTablev2
                      clearFilters={handleClearFilters}
                      onFilterSubmit={handleFilterSubmit}
                      filterColumnTableName={leads}
                      pagination={leads?.pagination}
                      onPaginationChange={handlePageChange}
                      rowsPerPage={rowsPerPage}
                      defaultColumnWidth={defaultColumnWidth}
                      data={formattedData}
                      filters={filters}
                      setFilters={setFilters}
                      customColumns={customColumns}
                      disableFilters={false}
                      onRowClicked={(row) => {
                        history.push(
                          `${INCOMLEND_INTERNAL_LINK}${LEADS}/${row.internal_name}`
                        );
                      }}
                    />
                ) : (
                  <NoData text="No Leads found" sx={{ margin: "auto" }} />
                )}
              </Grid>
            </Grid>
          )}
          {showApprovalModal && (
            <Modal
              open={showApprovalModal}
              message={
                <Typography fontWeight="bold">
                  Are you sure you want to update Sales Owner for this lead?
                </Typography>
              }
              modalHeaderSx={{ background: "transparent" }}
              onClose={() => {
                setShowApprovalModal(false);
                setSelectedLeadForUpdate(null);
                setNewSalesOwner(null);
              }}
              width={isMobile ? "90%" : "25%"}
              modalOverlaySx={{
                backgroundColor: "#fff !important",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "4ch",
                padding: "2ch 3ch",
              }}
              height="auto"
              closeButton
              primary={{
                children: "Confirm",
                color: "primary",
                onClick: () => {
                  if (!selectedLeadForUpdate?.account_id || !newSalesOwner) {
                    toast("Missing data to update", { type: "error" });
                    return;
                  }

                  // Close modal and show row loader
                  setShowApprovalModal(false);
                  setLoadingRowId(selectedLeadForUpdate.account_id);

                  updateAccount({
                    id: selectedLeadForUpdate.account_id,
                    sales_lead: newSalesOwner,
                  })
                    .unwrap()
                    .then(() => {
                      toast("Successfully updated Sales Owner", { type: "success" });
                    })
                    .catch((error) => {
                      toast(`Something went wrong: ${error?.message || "Error"}`, {
                        type: "error",
                      });
                    })
                    .finally(() => {
                      setLoadingRowId(null);
                      setSelectedLeadForUpdate(null);
                      setNewSalesOwner(null);
                      refetchLeadsWithoutReset();
                    });
                },
              }}
              secondary={{
                children: "Cancel",
                color: "secondary",
                onClick: () => {
                  setShowApprovalModal(false);
                  setSelectedLeadForUpdate(null);
                  setNewSalesOwner(null);
                },
              }}
            />
          )}
        </Layout>
      </Box>
    </Slide>
  );
};
export default Leads;
